import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  DocumentCommentRow,
  DocumentDetail,
  DocumentVersionRow,
  Paginated,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// Subscribe / unsubscribe — toggles a DocumentSubscription row.
// ---------------------------------------------------------------------------

export function useSubscribeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<DocumentDetail>(`/documents/${id}/subscribe/`);
      return data;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["documents", "detail", id] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useUnsubscribeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<DocumentDetail>(`/documents/${id}/unsubscribe/`);
      return data;
    },
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ["documents", "detail", id] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Comments — paginated list + create/edit/delete
// ---------------------------------------------------------------------------

export function useDocumentComments(documentId: string | undefined) {
  return useQuery<Paginated<DocumentCommentRow>>({
    enabled: documentId != null && !Number.isNaN(documentId),
    queryKey: ["document-comments", documentId],
    queryFn: async () => {
      const { data } = await api.get<Paginated<DocumentCommentRow>>(
        `/document-comments/?document=${documentId}`,
      );
      return data;
    },
  });
}

export function useCreateDocumentComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { document: string; body: string; reply_to?: string }) => {
      const { data } = await api.post<DocumentCommentRow>("/document-comments/", input);
      return data;
    },
    onSuccess: (_c, vars) => {
      qc.invalidateQueries({ queryKey: ["document-comments", vars.document] });
      qc.invalidateQueries({ queryKey: ["documents", "detail", vars.document] });
    },
  });
}

export function useUpdateDocumentComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; document: string; body: string }) => {
      const { data } = await api.patch<DocumentCommentRow>(
        `/document-comments/${input.id}/`,
        { body: input.body },
      );
      return data;
    },
    onSuccess: (_c, vars) =>
      qc.invalidateQueries({ queryKey: ["document-comments", vars.document] }),
  });
}

export function useDeleteDocumentComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; document: string }) => {
      await api.delete(`/document-comments/${input.id}/`);
    },
    onSuccess: (_x, vars) => {
      qc.invalidateQueries({ queryKey: ["document-comments", vars.document] });
      qc.invalidateQueries({ queryKey: ["documents", "detail", vars.document] });
    },
  });
}

// ---------------------------------------------------------------------------
// Versions — read-only history, returned unpaginated (small per doc).
// ---------------------------------------------------------------------------

export function useDocumentVersions(documentId: string | undefined) {
  return useQuery<DocumentVersionRow[]>({
    enabled: documentId != null && !Number.isNaN(documentId),
    queryKey: ["document-versions", documentId],
    queryFn: async () => {
      const { data } = await api.get<DocumentVersionRow[]>(
        `/document-versions/?document=${documentId}`,
      );
      return data;
    },
  });
}
